import { useEffect, useState } from 'react'
import { reservationService, Reservation, ReservationFilters } from '@/services/reservationService'
import useAppConfigStore from '@/stores/appConfigStore'
import { authService } from '@/services/authService'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import DataTable, { DataTableColumn } from '@/components/ui/DataTable'
import { Eye, Calendar, User, Store, Package, Tag, Banknote } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const ReservationsPage = () => {
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { systemConfig, initializeAppConfig, loading: configLoading } = useAppConfigStore()
  const navigate = useNavigate()

  const loadReservations = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get user role and merchant context
      const isAdmin = authService.isAdmin()
      const merchantContext = authService.getMerchantContext()
      
      // Prepare filters based on user role
      const filters: ReservationFilters = {}
      if (!isAdmin && merchantContext?.merchantId) {
        filters.merchant_id = merchantContext.merchantId
      }
      
      const response = await reservationService.getAll(filters)
      if (response.success) {
        setReservations(response.data)
      } else {
        setError('Échec du chargement des réservations')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReservations()
  }, [])

  useEffect(() => {
    // Initialiser la configuration système si elle n'est pas déjà chargée
    if (!systemConfig && !configLoading) {
      initializeAppConfig()
    }
  }, [systemConfig, configLoading, initializeAppConfig])

  const formatCurrency = (amount: number | string) => {
    const currencyCode = systemConfig?.default_currency || 'MAD'
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode
    }).format(numericAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadgeVariant = (status: Reservation['status']) => {
    switch (status) {
      case 'pending': return 'secondary'
      case 'confirmed': return 'default'
      case 'cancelled': return 'destructive'
      case 'completed': return 'outline'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: Reservation['status']) => {
    switch (status) {
      case 'pending': return 'En attente'
      case 'confirmed': return 'Confirmée'
      case 'cancelled': return 'Annulée'
      case 'completed': return 'Terminée'
      default: return status
    }
  }

  const getItemType = (reservation: Reservation) => {
    if (reservation.basket_id) return 'Panier'
    if (reservation.product_id) return 'Produit'
    if (reservation.offer_id) return 'Offre'
    return 'Inconnu'
  }

  const getUserName = (reservation: Reservation) => {
    if (reservation.user_first_name && reservation.user_last_name) {
      return `${reservation.user_first_name} ${reservation.user_last_name}`
    }
    return `User ${reservation.user_id.substring(0, 8)}...`
  }

  // Define columns for DataTable
  const columns: DataTableColumn<Reservation>[] = [
    {
      key: 'reservation_id',
      label: 'ID Réservation',
      sortable: true,
      render: (value: string) => (
        <div className="font-mono text-sm">
          {value.substring(0, 8)}...
        </div>
      )
    },
    {
      key: 'user',
      label: 'Utilisateur',
      sortable: true,
      render: (_value: any, row: Reservation) => (
        <div className="flex items-center">
          <User className="h-4 w-4 mr-2 text-gray-400" />
          <div>
            <div className="font-medium">{getUserName(row)}</div>
            <div className="text-xs text-gray-500">{row.user_id.substring(0, 8)}...</div>
          </div>
        </div>
      )
    },
    {
      key: 'merchant',
      label: 'Commerçant',
      sortable: true,
      render: (_value: any, row: Reservation) => (
        <div className="flex items-center">
          <Store className="h-4 w-4 mr-2 text-gray-400" />
          <div>
            <div className="font-medium">{row.merchant_name || 'N/D'}</div>
            <div className="text-xs text-gray-500">{row.merchant_id.substring(0, 8)}...</div>
          </div>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type & Détails',
      sortable: true,
      render: (_value: any, row: Reservation) => (
        <div>
          <div className="flex items-center">
            {row.product_id ? (
              <Package className="h-4 w-4 mr-2 text-blue-500" />
            ) : row.offer_id ? (
              <Tag className="h-4 w-4 mr-2 text-green-500" />
            ) : (
              <Package className="h-4 w-4 mr-2 text-gray-500" />
            )}
            <span className="font-medium">{getItemType(row)}</span>
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {row.product_id ? `Produit: ${row.product_id.substring(0, 8)}...` : 
             row.offer_id ? `Offre: ${row.offer_id.substring(0, 8)}...` : 
             'Panier'}
          </div>
        </div>
      )
    },
    {
      key: 'quantity',
      label: 'Quantité',
      sortable: true,
      render: (value: number) => (
        <div className="text-center font-medium">{value}</div>
      )
    },
    {
      key: 'total_amount',
      label: 'Montant',
      sortable: true,
      render: (value: number | string) => (
        <div className="flex items-center">
          <Banknote className="h-4 w-4 mr-2 text-green-500" />
          <div className="font-medium">{formatCurrency(value)}</div>
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
          <div className="text-sm">{formatDate(value)}</div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value: Reservation['status']) => (
        <Badge variant={getStatusBadgeVariant(value)}>
          {getStatusLabel(value)}
        </Badge>
      )
    }
  ]

  // Define actions for DataTable
  const actions = [
    {
      label: 'Voir détails',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: Reservation) => navigate(`/reservations/${row.reservation_id}`),
      variant: 'outline' as const
    }
  ]

  // Calculate statistics
  const totalReservations = reservations.length
  const totalRevenue = reservations.reduce((sum, r) => {
    const amount = typeof r.total_amount === 'string' ? parseFloat(r.total_amount) : r.total_amount
    return sum + amount
  }, 0)
  const confirmedReservations = reservations.filter(r => r.status === 'confirmed').length
  const pendingReservations = reservations.filter(r => r.status === 'pending').length

  // Get user role and merchant context for UI display
  const isAdmin = authService.isAdmin()
  const merchantContext = authService.getMerchantContext()
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">Réservations</h1>
            {!isAdmin && merchantContext && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {merchantContext.merchantName}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {isAdmin
              ? 'Gérez toutes les réservations de la plateforme'
              : `Affiche uniquement les réservations de ${merchantContext?.merchantName || 'votre commerce'}`}
          </p>
        </div>
        <Button onClick={loadReservations} disabled={loading}>
          {loading ? 'Chargement...' : 'Actualiser'}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total réservations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalReservations}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Chiffre d'affaires</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Confirmées</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{confirmedReservations}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">En attente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingReservations}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Liste des réservations</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 text-destructive rounded-md">
              {error}
            </div>
          )}

          <DataTable
            data={reservations}
            columns={columns}
            actions={actions}
            loading={loading}
            emptyMessage="Aucune réservation trouvée"
            search={{
              placeholder: "Rechercher des réservations...",
              onSearch: (query: string) => {
                // Note: The DataTable handles search internally, but we need to provide this callback
                // For now, we'll just log it or implement filtering if needed
                console.log('Search query:', query)
              }
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export default ReservationsPage
